(() => {
  const FORM_SELECTOR = '.f24-header-preview__search, .f24-hero-v3-search, .f24-floating-search';
  const MIN_QUERY_LENGTH = 2;
  const DEBOUNCE_MS = 180;
  const SECTION_ID = 'f24-full-search-suggestions';
  const cache = new Map();
  let panelCounter = 0;

  const shopRoot = () => {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const normalizeQuery = (query) =>
    window.F24SearchExpander?.queryForShopify?.(query)
    || window.F24SearchNormalizer?.normalize?.(query)
    || String(query || '').trim();

  const fullSearchUrl = (query, page = null) => {
    const params = new URLSearchParams();
    params.set('q', normalizeQuery(query));
    params.set('type', 'product');
    params.set('options[prefix]', 'last');
    params.set('options[unavailable_products]', 'last');
    if (page && page > 1) params.set('page', String(page));
    params.set('section_id', SECTION_ID);
    return `${shopRoot()}search?${params.toString()}`;
  };

  const resultsPageUrl = (query) => {
    const params = new URLSearchParams();
    params.set('q', normalizeQuery(query));
    params.set('type', 'product');
    params.set('options[prefix]', 'last');
    params.set('options[unavailable_products]', 'last');
    return `${shopRoot()}search?${params.toString()}`;
  };

  const createText = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    node.textContent = text;
    return node;
  };

  class F24LiveSearch {
    constructor(form) {
      this.form = form;
      this.input = form.querySelector('input[type="search"][name="q"]');
      if (!this.input || form.closest('predictive-search')) return;

      this.controller = null;
      this.loadMoreController = null;
      this.timer = null;
      this.activeIndex = -1;
      this.items = [];
      this.lastQuery = '';
      this.raf = 0;
      this.id = `F24LiveSearch-${++panelCounter}`;
      this.state = null;

      this.panel = document.createElement('div');
      this.panel.id = this.id;
      this.panel.className = 'f24-live-search-panel';
      this.panel.setAttribute('role', 'listbox');
      this.panel.setAttribute('aria-label', 'Suchvorschläge');
      document.body.appendChild(this.panel);

      this.input.setAttribute('autocomplete', 'off');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-controls', this.id);
      this.input.setAttribute('aria-expanded', 'false');

      this.bindEvents();
    }

    bindEvents() {
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('focus', () => this.onFocus());
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.form.addEventListener('submit', () => this.close());

      document.addEventListener('pointerdown', (event) => {
        if (!this.form.contains(event.target) && !this.panel.contains(event.target)) this.close();
      });

      window.addEventListener('resize', () => this.schedulePosition());
      window.addEventListener('scroll', () => this.schedulePosition(), true);
    }

    onInput() {
      const query = this.input.value.trim();
      this.lastQuery = query;
      this.activeIndex = -1;
      window.clearTimeout(this.timer);

      if (query.length < MIN_QUERY_LENGTH) {
        this.abort();
        this.close();
        return;
      }

      const key = normalizeQuery(query).toLocaleLowerCase('de-CH');
      if (cache.has(key)) {
        this.render(query, cache.get(key));
        return;
      }

      this.timer = window.setTimeout(() => this.fetchResults(query), DEBOUNCE_MS);
    }

    onFocus() {
      const query = this.input.value.trim();
      if (query.length < MIN_QUERY_LENGTH) return;
      const key = normalizeQuery(query).toLocaleLowerCase('de-CH');
      const cached = cache.get(key);
      if (cached) this.render(query, cached);
      else this.fetchResults(query);
    }

    onKeydown(event) {
      if (!this.isOpen()) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.moveActive(1);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.moveActive(-1);
        return;
      }

      if (event.key === 'Enter' && this.activeIndex >= 0 && this.items[this.activeIndex]) {
        event.preventDefault();
        window.location.href = this.items[this.activeIndex].href;
      }
    }

    moveActive(step) {
      if (!this.items.length) return;
      this.activeIndex = (this.activeIndex + step + this.items.length) % this.items.length;
      this.items.forEach((item, index) => {
        const active = index === this.activeIndex;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      const activeItem = this.items[this.activeIndex];
      this.input.setAttribute('aria-activedescendant', activeItem.id);
      activeItem.scrollIntoView({ block: 'nearest' });
    }

    abort() {
      this.controller?.abort();
      this.loadMoreController?.abort();
      this.controller = null;
      this.loadMoreController = null;
    }

    async fetchPage(query, page = 1, controller = null) {
      const response = await fetch(fullSearchUrl(query, page), {
        headers: { Accept: 'text/html' },
        signal: controller?.signal,
      });
      if (!response.ok) throw new Error(`Full search failed: ${response.status}`);

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const root = doc.querySelector('[data-f24-full-search-results]');
      if (!root) throw new Error('Full search section missing');

      return {
        items: Array.from(root.querySelectorAll('[data-f24-search-item]')).map((node) => node.outerHTML),
        total: Number.parseInt(root.dataset.totalResults || '0', 10) || 0,
        page: Number.parseInt(root.dataset.page || String(page), 10) || page,
        pages: Number.parseInt(root.dataset.pages || '1', 10) || 1,
      };
    }

    async fetchResults(query) {
      this.abort();
      this.controller = new AbortController();

      try {
        let data = await this.fetchPage(query, 1, this.controller);
        if (!data.total && window.F24SearchExpander?.candidates) {
          const candidates = window.F24SearchExpander.candidates(query).slice(1);
          for (const candidate of candidates) {
            if (this.controller?.signal.aborted) return;
            const found = await this.fetchPage(candidate, 1, this.controller);
            if (found.total) { data = { ...found, resolvedQuery: candidate }; break; }
          }
        }
        const key = normalizeQuery(query).toLocaleLowerCase('de-CH');
        cache.set(key, data);
        if (this.input.value.trim() === query) this.render(query, data);
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.warn('[F24 Vollsuche] Suchvorschläge konnten nicht geladen werden.', error);
          this.close();
        }
      } finally {
        this.controller = null;
      }
    }

    render(query, data) {
      this.panel.replaceChildren();
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');

      this.state = {
        query,
        total: data.total,
        page: data.page,
        pages: data.pages,
        shown: 0,
        resolvedQuery: data.resolvedQuery || query,
      };

      const section = document.createElement('div');
      section.className = 'f24-live-search__section';
      section.dataset.f24FullProducts = 'true';
      section.appendChild(
        createText(
          'div',
          'f24-live-search__heading',
          data.total === 1 ? '1 Produkt gefunden' : `${data.total} Produkte gefunden`
        )
      );

      data.items.forEach((html) => {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = html;
        const item = wrapper.firstElementChild;
        if (item) section.appendChild(item);
      });

      this.panel.appendChild(section);
      this.state.shown = section.querySelectorAll('[data-f24-search-item]').length;

      if (!this.state.shown) {
        section.appendChild(createText('div', 'f24-live-search__empty', 'Keine passenden Produkte gefunden.'));
      }

      this.renderFooterControls();
      this.refreshItems();
      this.open();
    }

    refreshItems() {
      this.items = Array.from(this.panel.querySelectorAll('a[data-f24-search-item]'));
      this.items.forEach((item, index) => {
        item.id = `${this.id}-item-${index}`;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        item.addEventListener('mouseenter', () => {
          this.activeIndex = index;
          this.items.forEach((entry, entryIndex) => entry.classList.toggle('is-active', entryIndex === index));
        });
      });
    }

    renderFooterControls() {
      this.panel.querySelector('.f24-live-search__more')?.remove();
      this.panel.querySelector('.f24-live-search__all')?.remove();

      if (this.state && this.state.page < this.state.pages) {
        const more = document.createElement('button');
        more.type = 'button';
        more.className = 'f24-live-search__more';
        more.textContent = `Weitere Treffer laden (${this.state.shown} von ${this.state.total})`;
        more.addEventListener('click', () => this.loadMore(more));
        this.panel.appendChild(more);
      }

      const all = document.createElement('a');
      all.className = 'f24-live-search__all';
      const searchTerm = this.state?.resolvedQuery || this.input.value.trim();
      all.href = resultsPageUrl(searchTerm);
      if (this.state?.resolvedQuery && this.state.resolvedQuery !== this.state.query) {
        const url = new URL(all.href, location.href);
        url.searchParams.set('f24q', this.state.query);
        all.href = url.toString();
      }
      all.textContent = this.state?.total
        ? `Alle ${this.state.total} Ergebnisse anzeigen`
        : `Alle Ergebnisse anzeigen`;
      this.panel.appendChild(all);
    }

    async loadMore(button) {
      if (!this.state || this.state.page >= this.state.pages) return;

      button.disabled = true;
      button.textContent = 'Weitere Treffer werden geladen …';
      this.loadMoreController?.abort();
      this.loadMoreController = new AbortController();
      const nextPage = this.state.page + 1;
      const query = this.state.resolvedQuery || this.state.query;

      try {
        const data = await this.fetchPage(query, nextPage, this.loadMoreController);
        if (!this.state || this.state.query !== query) return;

        const section = this.panel.querySelector('[data-f24-full-products]');
        if (!section) return;

        data.items.forEach((html) => {
          const wrapper = document.createElement('div');
          wrapper.innerHTML = html;
          const item = wrapper.firstElementChild;
          if (item) section.appendChild(item);
        });

        this.state.page = data.page;
        this.state.pages = data.pages;
        this.state.total = data.total;
        this.state.shown = section.querySelectorAll('[data-f24-search-item]').length;
        this.refreshItems();
        this.renderFooterControls();
        this.schedulePosition();
      } catch (error) {
        if (error?.name !== 'AbortError') {
          button.disabled = false;
          button.textContent = 'Weitere Treffer laden';
        }
      } finally {
        this.loadMoreController = null;
      }
    }

    isOpen() {
      return this.panel.classList.contains('is-open');
    }

    open() {
      this.panel.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
      this.schedulePosition();
    }

    close() {
      this.panel.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
      this.items.forEach((item) => item.classList.remove('is-active'));
    }

    schedulePosition() {
      if (!this.isOpen()) return;
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => this.position());
    }

    position() {
      if (!this.isOpen()) return;
      const rect = this.form.getBoundingClientRect();
      const viewportPadding = 12;
      const width = Math.min(Math.max(rect.width, 320), window.innerWidth - viewportPadding * 2);
      const maxLeft = window.scrollX + window.innerWidth - width - viewportPadding;
      const left = Math.max(window.scrollX + viewportPadding, Math.min(window.scrollX + rect.left, maxLeft));
      this.panel.style.width = `${Math.max(0, width)}px`;
      this.panel.style.left = `${left}px`;
      this.panel.style.top = `${window.scrollY + rect.bottom + 8}px`;
    }
  }

  const init = () => {
    document.querySelectorAll(FORM_SELECTOR).forEach((form) => {
      if (!form.dataset.f24LiveSearchReady) {
        form.dataset.f24LiveSearchReady = 'true';
        new F24LiveSearch(form);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
